import moment from 'moment'
import * as jspdf from 'jspdf'
import { END_CHAT_MESSAGE, START_CHAT_MESSAGE } from './helpDeskSession'

const SKYNICHE_LOGO = `${process.env.PUBLIC_URL || ''}/static/brand/logo_full.jpg`

const createPdf = (options) => {
  const Pdf =
    (typeof jspdf.jsPDF === 'function' && jspdf.jsPDF) ||
    (typeof jspdf.default === 'function' && jspdf.default) ||
    (typeof jspdf.default?.jsPDF === 'function' && jspdf.default.jsPDF)
  return new Pdf(options)
}

export const CHAT_NOTIFICATIONS_KEY = 'signwiseHelpDeskChatNotifications'
const HELP_DESK_USER_ID = 39
const USER_NAME_COLOR = [196, 92, 38]
const AGENT_NAME_COLOR = [33, 43, 54]
const TEXT_COLOR = [33, 43, 54]
const META_COLOR = [99, 115, 129]
const LINE_HEIGHT = 6.2

export const getChatNotificationsEnabled = () => {
  try {
    return localStorage.getItem(CHAT_NOTIFICATIONS_KEY) !== '0'
  } catch (error) {
    return true
  }
}

export const setChatNotificationsEnabled = (enabled) => {
  try {
    localStorage.setItem(CHAT_NOTIFICATIONS_KEY, enabled ? '1' : '0')
  } catch (error) {
    console.log('Could not save chat notification preference', error)
  }
}

const stripHtml = (value) =>
  String(value || '')
    .replace(/<\/p><p>/g, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const formatClock = (createdAt) =>
  createdAt && moment.unix(createdAt).isValid()
    ? moment.unix(createdAt).format('HH:mm:ss')
    : moment().format('HH:mm:ss')

const systemLabel = (item) => {
  const text = stripHtml(item?.text || item?.message)
  const addedName = text.match(/^(.*) was added to the chat$/i)?.[1]?.trim()
  const leftName = text.match(/^(.*) left the chat$/i)?.[1]?.trim()
  if (addedName) return `${addedName} joined the chat`
  if (leftName) return `${leftName} left the chat`
  if (!text || text === START_CHAT_MESSAGE) return START_CHAT_MESSAGE
  return text
}

const isHelpDeskSender = (item, treatYouAsAgent) => {
  if (Number(item?.sender_id) === HELP_DESK_USER_ID) return true
  const sender = String(item?.sender || '')
  if (sender === 'Help Desk') return true
  if (treatYouAsAgent && sender === 'You') return true
  return false
}

const ensurePageSpace = (doc, y, needed, margin) => {
  const pageHeight = doc.internal.pageSize.getHeight()
  if (y + needed <= pageHeight - margin) return y
  doc.addPage()
  return margin
}

const loadLogoData = () =>
  new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        canvas.getContext('2d').drawImage(img, 0, 0)
        const isJpeg = /\.jpe?g(\?|$)/i.test(String(img.src))
        resolve({
          data: canvas.toDataURL(isJpeg ? 'image/jpeg' : 'image/png'),
          format: isJpeg ? 'JPEG' : 'PNG',
          width: img.naturalWidth,
          height: img.naturalHeight,
        })
      } catch (error) {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = SKYNICHE_LOGO
  })

const fileNameFromUrl = (url = '') => {
  try {
    const path = String(url).split('?')[0]
    const raw = decodeURIComponent(path.split('/').pop() || '')
    return raw.replace(/^\d{10,}_/, '') || 'Attachment'
  } catch (error) {
    return 'Attachment'
  }
}

const getAttachmentUrl = (item) =>
  item?.attachment || item?.attachment_url || null

const getAttachmentKind = (item, url = '') => {
  const type = String(item?.contentType || item?.message_type || '').toLowerCase()
  const src = String(url || '')
  if (type === 'image' || /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(src)) return 'image'
  if (type === 'video' || /\.(mp4|webm|ogg|mov|m4v|avi|mkv)(\?|$)/i.test(src)) return 'video'
  if (/\.pdf(\?|$)/i.test(src)) return 'pdf'
  if (/\.(xlsx?|csv)(\?|$)/i.test(src)) return 'excel'
  if (/\.(docx?|rtf|txt)(\?|$)/i.test(src)) return 'document'
  if (/\.(pptx?)(\?|$)/i.test(src)) return 'presentation'
  if (type === 'file' || type === 'gif' || src) return 'file'
  return null
}

const attachmentKindLabel = (kind) => {
  if (kind === 'image') return 'Image'
  if (kind === 'video') return 'Video'
  if (kind === 'pdf') return 'PDF Document'
  if (kind === 'excel') return 'Excel Spreadsheet'
  if (kind === 'document') return 'Document'
  if (kind === 'presentation') return 'Presentation'
  return 'File'
}

const loadRemoteImageData = (url) =>
  new Promise((resolve) => {
    if (!url) {
      resolve(null)
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const maxSide = 1200
        const ratio = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight || 1))
        canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio))
        canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio))
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
        const isJpeg = /\.jpe?g(\?|$)/i.test(String(url)) || String(url).includes('image/jpeg')
        resolve({
          data: canvas.toDataURL(isJpeg ? 'image/jpeg' : 'image/png', 0.86),
          format: isJpeg ? 'JPEG' : 'PNG',
          width: canvas.width,
          height: canvas.height,
        })
      } catch (error) {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = url
  })

const fitPreviewSize = (image, maxWidth, maxHeight) => {
  const ratio = Number(image?.width) / Number(image?.height || 1)
  if (!ratio || !Number.isFinite(ratio)) {
    return { width: maxWidth, height: Math.min(maxHeight, 40) }
  }
  let width = Math.min(maxWidth, maxWidth)
  let height = width / ratio
  if (height > maxHeight) {
    height = maxHeight
    width = height * ratio
  }
  return { width, height }
}

const drawAttachmentPreview = async (doc, item, x, y, maxWidth, margin) => {
  const url = getAttachmentUrl(item)
  if (!url) return y
  const kind = getAttachmentKind(item, url)
  if (!kind) return y
  const fileName = fileNameFromUrl(url)

  let cursor = ensurePageSpace(doc, y, 8, margin)
  const cardX = x
  const cardWidth = Math.min(maxWidth, 90)

  if (kind === 'image') {
    const image = await loadRemoteImageData(url)
    if (image?.data) {
      const size = fitPreviewSize(image, cardWidth, 55)
      cursor = ensurePageSpace(doc, cursor, size.height + 14, margin)
      doc.setDrawColor(223, 227, 232)
      doc.setFillColor(255, 255, 255)
      doc.roundedRect(cardX, cursor - 3, size.width, size.height + 10, 2, 2, 'FD')
      try {
        doc.addImage(image.data, image.format || 'JPEG', cardX, cursor - 3, size.width, size.height)
      } catch (error) {
        // ignore addImage failures and continue with filename
      }
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(...META_COLOR)
      doc.text(`${fileName} · Preview`, cardX, cursor + size.height + 4, {
        maxWidth: size.width,
      })
      cursor += size.height + 10
    } else {
      cursor = ensurePageSpace(doc, cursor, 16, margin)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.setTextColor(...TEXT_COLOR)
      doc.text(`[Image] ${fileName}`, cardX, cursor)
      cursor += LINE_HEIGHT
      doc.setTextColor(2, 125, 194)
      doc.textWithLink('Open attachment', cardX, cursor, { url })
      cursor += LINE_HEIGHT + 1
    }
  } else {
    const label = attachmentKindLabel(kind)
    cursor = ensurePageSpace(doc, cursor, 24, margin)
    doc.setDrawColor(223, 227, 232)
    doc.setFillColor(244, 246, 248)
    doc.roundedRect(cardX, cursor - 4, cardWidth, 18, 2, 2, 'FD')
    doc.setFillColor(
      kind === 'pdf' ? 229 : kind === 'excel' ? 67 : kind === 'video' ? 94 : 96,
      kind === 'pdf' ? 57 : kind === 'excel' ? 160 : kind === 'video' ? 53 : 125,
      kind === 'pdf' ? 53 : kind === 'excel' ? 71 : kind === 'video' ? 177 : 139,
    )
    doc.roundedRect(cardX + 2, cursor - 1.5, 12, 12, 1.5, 1.5, 'F')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7)
    doc.setTextColor(255, 255, 255)
    const badge =
      kind === 'pdf'
        ? 'PDF'
        : kind === 'excel'
        ? 'XLS'
        : kind === 'video'
        ? 'VID'
        : kind === 'document'
        ? 'DOC'
        : 'FILE'
    doc.text(badge, cardX + 8, cursor + 5.5, { align: 'center' })
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(...TEXT_COLOR)
    doc.text(fileName, cardX + 17, cursor + 2.5, { maxWidth: cardWidth - 20 })
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(...META_COLOR)
    doc.text(`${label} · Preview`, cardX + 17, cursor + 7.5)
    cursor += 16
    doc.setFontSize(8)
    doc.setTextColor(2, 125, 194)
    doc.textWithLink('Open attachment', cardX, cursor, { url })
    cursor += LINE_HEIGHT + 1
  }

  return cursor + 1
}

const fitLogoSize = (logo, maxWidth, maxHeight) => {
  const ratio = Number(logo?.width) / Number(logo?.height || 1)
  if (!ratio || !Number.isFinite(ratio)) {
    return { width: maxWidth, height: maxHeight }
  }
  let width = maxWidth
  let height = width / ratio
  if (height > maxHeight) {
    height = maxHeight
    width = height * ratio
  }
  return { width, height }
}

const drawHeader = (doc, engagementId, logo) => {
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 18
  const size = logo ? fitLogoSize(logo, 42, 12) : { width: 0, height: 0 }
  const logoY = 8
  const textY = logo ? logoY + size.height / 2 + 1.6 : 16

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.setTextColor(...TEXT_COLOR)
  doc.text(engagementId, margin, textY)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.text('Chat Transcript', pageWidth / 2, textY, { align: 'center' })

  if (logo?.data) {
    doc.addImage(
      logo.data,
      logo.format || 'JPEG',
      pageWidth - margin - size.width,
      logoY,
      size.width,
      size.height,
    )
  }

  const lineY = Math.max(20, logoY + size.height + 3)
  doc.setDrawColor(33, 43, 54)
  doc.setLineWidth(0.35)
  doc.line(margin, lineY, pageWidth - margin, lineY)

  const offset = moment().format('Z')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(...META_COLOR)
  doc.text(`Time Zone - GMT [UTC ${offset}]`, margin, lineY + 6)
  doc.text(moment().format('D MMMM, YYYY'), pageWidth - margin, lineY + 6, {
    align: 'right',
  })
  return lineY + 14
}

const drawSystemLine = (doc, time, label, x, y, maxWidth, margin) => {
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  const lines = doc.splitTextToSize(`[${time}] [${label}]`, maxWidth)
  let cursor = y
  lines.forEach((line) => {
    cursor = ensurePageSpace(doc, cursor, LINE_HEIGHT, margin)
    doc.setTextColor(...TEXT_COLOR)
    doc.text(line, x, cursor)
    cursor += LINE_HEIGHT
  })
  return cursor
}

const drawChatLine = (doc, time, name, body, nameColor, x, y, maxWidth, margin) => {
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  const timeStr = `[${time}] `
  const nameStr = `${name}: `
  const timeWidth = doc.getTextWidth(timeStr)
  doc.setFont('helvetica', 'bold')
  const nameWidth = doc.getTextWidth(nameStr)
  const prefixWidth = timeWidth + nameWidth
  const bodyWidth = Math.max(50, maxWidth - prefixWidth)
  doc.setFont('helvetica', 'normal')
  const bodyLines = doc.splitTextToSize(body || '', bodyWidth)
  const rows = bodyLines.length ? bodyLines : ['']
  let cursor = ensurePageSpace(doc, y, LINE_HEIGHT, margin)

  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...TEXT_COLOR)
  doc.text(timeStr, x, cursor)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...nameColor)
  doc.text(nameStr, x + timeWidth, cursor)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...TEXT_COLOR)
  doc.text(rows[0], x + prefixWidth, cursor)
  cursor += LINE_HEIGHT

  for (let index = 1; index < rows.length; index += 1) {
    cursor = ensurePageSpace(doc, cursor, LINE_HEIGHT, margin)
    doc.text(rows[index], x + prefixWidth, cursor)
    cursor += LINE_HEIGHT
  }
  return cursor
}

export const downloadChatTranscript = async ({
  messages = [],
  title = 'Help Desk chat',
  treatYouAsAgent = false,
} = {}) => {
  const usable = (messages || []).filter((item) => !item?.isTyping)
  const logId =
    usable.find((item) => item?.chat_log_id)?.chat_log_id ||
    usable.find((item) => item?.id && !String(item.id).startsWith('local'))?.id ||
    moment().format('YYYYMMDDHHmm')
  const engagementId = `Engagement SW-${logId}`
  const logo = await loadLogoData()
  const doc = createPdf({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 18
  const maxWidth = pageWidth - margin * 2

  let y = drawHeader(doc, engagementId, logo)

  if (!usable.length) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(...META_COLOR)
    doc.text('No messages in this chat.', margin, y)
  } else {
    for (const item of usable) {
      const time = formatClock(item?.created_at)
      const type = item?.contentType || item?.message_type
      if (type === 'system') {
        y = drawSystemLine(doc, time, systemLabel(item), margin, y, maxWidth, margin)
        continue
      }
      if (type === 'end') {
        y = drawSystemLine(doc, time, END_CHAT_MESSAGE, margin, y, maxWidth, margin)
        continue
      }

      const attachmentUrl = getAttachmentUrl(item)
      const kind = getAttachmentKind(item, attachmentUrl)
      const sender = item?.sender || 'User'
      const caption = stripHtml(item?.text || item?.message)
      const fileName = attachmentUrl ? fileNameFromUrl(attachmentUrl) : ''
      const captionIsFileName =
        caption && fileName && caption.toLowerCase() === fileName.toLowerCase()
      let body = ''
      if (attachmentUrl) {
        body = caption && !captionIsFileName ? caption : `[${attachmentKindLabel(kind)} attachment]`
      } else {
        body = caption || (type === 'image' ? 'Sent a photo' : '')
      }
      const nameColor = isHelpDeskSender(item, treatYouAsAgent)
        ? AGENT_NAME_COLOR
        : USER_NAME_COLOR

      if (body) {
        y = drawChatLine(doc, time, sender, body, nameColor, margin, y, maxWidth, margin)
      }

      if (attachmentUrl) {
        y = await drawAttachmentPreview(doc, item, margin + 4, y, maxWidth - 4, margin)
      }
    }
  }

  const fileDate = moment().format('DD_MMM_YYYY')
  doc.save(`Chat_Transcript_${fileDate}.pdf`)
  return engagementId
}
